// /client/src/components/NewsletterSection.js
import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { API_BASE } from "../utils/env";

const NewsletterSection = () => {
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState(""); // bots will fill this
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState({ type: null, message: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: null, message: "" });

    const trimmed = email.trim();
    if (!trimmed) {
      setStatus({ type: "error", message: "Please enter your email address." });
      return;
    }

    const emailRegex = /^\S+@\S+\.\S+$/;
    if (!emailRegex.test(trimmed)) {
      setStatus({
        type: "error",
        message: "Please enter a valid email address.",
      });
      return;
    }

    setSending(true);
    try {
      const res = await fetch(`${API_BASE}/api/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, honeypot }),
      });

      const contentType = res.headers.get("content-type") || "";
      let data = null;

      if (contentType.includes("application/json")) {
        data = await res.json();
      } else {
        const text = await res.text();
        throw new Error(
          `Unexpected response: ${res.status} ${text.slice(0, 200)}`
        );
      }

      if (!res.ok) {
        throw new Error(data?.error || "send_failed");
      }

      setStatus({
        type: "success",
        message:
          "You’re on the Heat List! We’ll email you when new drops hit. 🔥",
      });
      setEmail("");
      setHoneypot("");
    } catch (err) {
      console.error("[newsletter] error:", err);
      setStatus({
        type: "error",
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Grid size={{ xs: 12 }} justifyContent="center">
      <Box
        component="section"
        sx={{
          backgroundColor: "#E94E1B",
          py: 4,
          color: "#fff",
          textAlign: "center",
        }}
      >
        <Container>
          <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
            Get the Heat First
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 4 }}>
            Be the first to know about new drops, discounts, and recipes.
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            {/* Honeypot field (hidden from users, visible to bots) */}
            <input
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              style={{ display: "none" }}
              autoComplete="off"
              tabIndex={-1}
            />

            <Grid container justifyContent="center" spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <TextField
                  placeholder="Enter your email"
                  fullWidth
                  variant="filled"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{
                    backgroundColor: "#FFF3E6",
                    borderRadius: 1,
                    input: { color: "#333" },
                  }}
                  required
                  type="email"
                />
              </Grid>
              <Grid
                size={{ xs: 12, sm: "auto" }}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Button
                  type="submit"
                  variant="contained"
                  disabled={sending}
                  sx={{
                    backgroundColor: "#8B2B17",
                    color: "#fff",
                    height: "48px",
                    fontWeight: "bold",
                    px: 4,
                    "&:hover": {
                      backgroundColor: "#6c2112",
                    },
                  }}
                  startIcon={sending ? <CircularProgress size={18} /> : null}
                >
                  {sending ? "Subscribing..." : "Subscribe"}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {status.message && (
            <Box sx={{ mt: 3, maxWidth: 480, mx: "auto" }}>
              <Alert severity={status.type === "success" ? "success" : "error"}>
                {status.message}
              </Alert>
            </Box>
          )}
        </Container>
      </Box>
    </Grid>
  );
};

export default NewsletterSection;
